import { StyleSheet, css } from 'aphrodite';
import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { useStore } from 'react-redux';

import ToolTips from 'components/ToolTips';
import { generateTextID, hexToRGBA } from 'utilities';
import { ArrowDropUp, ArrowDropDown } from 'utilities/icons';

export default injectIntl(TextInput);

function TextInput({
    autoComplete,
    autoFocus,
    disabled,
    disabledStyling,
    readOnly,
    fontSize = 15,
    hideNumberArrows,
    intl,
    margin,
    max,
    maxLength,
    min,
    name,
    noBorder,
    noPadding,
    onBlurProp,
    onChange,
    onFocusProp,
    onKeyDown,
    onKeyUp,
    optionalText,
    placeholder = '',
    required,
    showMaxLength,
    showRequired,
    theme,
    title = '',
    titleFontSize,
    tooltip,
    type = 'text',
    value = 'Value',
    width = 'auto'
}) {
    const [randomID, setRandomID] = useState(generateTextID());
    const [requiredAlert, setRequiredAlert] = useState(false);
    const store = useStore();

    if (!theme) {
        const {
            app: { theme: appTheme }
        } = store.getState();
        theme = appTheme;
    }

    const styleSheet = styles(theme, requiredAlert, fontSize, titleFontSize, disabledStyling);

    if (showRequired && !requiredAlert && value === '') {
        setRequiredAlert(true);
        return;
    }
    if (showRequired && requiredAlert && value !== '') {
        setRequiredAlert(false);
        return;
    }

    const stepUpDownInput = (id, direction) => {
        const el = document.getElementById(id);
        if (!el) return;
        direction === 'up' ? el.stepUp() : el.stepDown();
    };

    return (
        <div
            className={css(styleSheet.inputWrapper)}
            style={{ width: width, margin: margin || (title !== '' ? '7px 0' : '0 0 7px 0') }}>
            {title && title !== '' && randomID && (
                <div className={css(styleSheet.titleTop)} id={randomID}>
                    {title}
                    {required && ` (${intl.formatMessage({ id: 'form.required' })})`}
                    {optionalText && ` ${intl.formatMessage({ id: 'form.option' })}`}
                </div>
            )}
            {title && title !== '' && tooltip && tooltip !== '' && randomID && (
                <ToolTips target={randomID} message={tooltip} />
            )}
            <input
                className={css(styleSheet.inputClass) + ' inputClassComponent'}
                onFocus={() => {
                    setRequiredAlert(false);
                    onFocusProp && onFocusProp();
                }}
                onBlur={(e) => {
                    required && setRequiredAlert(value === '');
                    onBlurProp && onBlurProp(e);
                }}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
                style={{
                    fontSize: fontSize,
                    margin: title !== '' ? '7px auto 0 auto' : '0 auto',
                    padding: title !== '' ? '10px 0 7px 0' : noPadding ? '0' : '7px 0',
                    color: !requiredAlert ? theme.color.fontColor : 'red',
                    transition: '.2s color ease-in-out, .2s width ease-in-out, .2s border-bottom ease-in-out',
                    border: 'none',
                    borderBottom: noBorder
                        ? 'none'
                        : readOnly
                        ? `1px dashed ${!requiredAlert ? theme.color.borderColor : 'red'}`
                        : `1px solid ${!requiredAlert ? theme.color.borderColor : 'red'}`,
                    display: 'block',
                    width: '100%',
                    outline: 'none',
                    background: 'none',
                    opacity: disabledStyling ? 0.5 : 1,
                    cursor: readOnly ? 'default' : 'text'
                }}
                id={'textInput' + randomID}
                name={name}
                type={type}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                max={max}
                maxLength={maxLength}
                min={min}
                disabled={disabled ? true : false}
                readOnly={readOnly ? true : false}
                autoFocus={autoFocus ? true : false}
                autoComplete={autoComplete ? autoComplete : null}
            />
            {maxLength && showMaxLength && (
                <div className={css(styleSheet.characterCount)}>
                    {value.length} / {maxLength}
                </div>
            )}
            {type === 'number' && !hideNumberArrows && (
                <div className={css(styleSheet.numberArrows)}>
                    <div
                        className={css(styleSheet.numberArrowUp)}
                        onClick={disabled ? null : () => stepUpDownInput('textInput' + randomID, 'up')}>
                        <ArrowDropUp
                            width={'100%'}
                            height={3}
                            fill={!requiredAlert ? theme.color.fontColor : 'red'}
                            pathStyle={{ transition: '.2s fill ease-in-out' }}
                            value={value}
                        />
                    </div>
                    <div className={css(styleSheet.numberArrowSeparator)}></div>
                    <div
                        className={css(styleSheet.numberArrowDown)}
                        onClick={disabled ? null : () => stepUpDownInput('textInput' + randomID, 'down')}>
                        <ArrowDropDown
                            width={'100%'}
                            height={3}
                            fill={!requiredAlert ? theme.color.fontColor : 'red'}
                            pathStyle={{ transition: '.2s fill ease-in-out' }}
                            value={value}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = (theme, requiredAlert, fontSize, titleFontSize, disabledStyling) =>
    StyleSheet.create({
        inputWrapper: {
            position: 'relative',
            display: 'grid',
            transition: '.2s color ease-in-out, .2s width ease-in-out'
        },
        titleTop: {
            position: 'absolute',
            top: -4,
            backgroundColor: theme.color.headerColor,
            width: '100%',
            textAlign: 'left',
            color: !requiredAlert ? theme.color.themeColor : 'red',
            transition: '.2s color ease-in-out',
            fontSize: titleFontSize || 12,
            opacity: disabledStyling ? 0.5 : 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden'
        },
        inputClass: {
            '::placeholder': {
                transition: '.2s color ease-in-out, .2s width ease-in-out',
                color: !requiredAlert ? theme.color.subFontColor : `rgba(255,0,0,0.55)`,
                fontSize: fontSize
            },
            ':-webkit-autofill': {
                '-webkit-box-shadow': `0px 0px 0px 50px ${theme.color.compColor} inset !important`,
                '-webkit-text-fill-color': !requiredAlert ? theme.color.fontColor : 'red',
                caretColor: !requiredAlert ? theme.color.fontColor : 'red'
            }
        },
        characterCount: {
            position: 'absolute',
            right: 0,
            bottom: 7,
            marginRight: 10
        },
        numberArrows: {
            position: 'absolute',
            right: 2,
            bottom: 9,
            backgroundColor: !requiredAlert
                ? theme && hexToRGBA(theme.color.borderColor, 0.3)
                : hexToRGBA('#FF0000', 0.3),
            transition: '.2s background-color ease-in-out',
            borderRadius: 5,
            width: 21,
            height: 21,
            userSelect: 'none',
            overflow: 'hidden'
        },
        numberArrowUp: {
            display: 'grid',
            cursor: 'pointer',
            placeItems: 'center',
            height: 10,
            width: '100%',
            ':hover': {
                backgroundColor: 'rgba(0,0,0,0.04)'
            }
        },
        numberArrowDown: {
            display: 'grid',
            cursor: 'pointer',
            placeItems: 'center',
            height: 10,
            width: '100%',
            ':hover': {
                backgroundColor: 'rgba(0,0,0,0.06)'
            }
        },
        numberArrowSeparator: {
            borderTop: `1px solid ${!requiredAlert ? theme && theme.color.borderColor : hexToRGBA('#FF0000', 0.5)}`,
            transition: '.2s border-top ease-in-out'
        }
    });
